import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  Chip,
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import { Avatar } from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import CloseIcon from "@mui/icons-material/Close";
import { exportToExcel, ExcelData } from "../../../utils/excelExport";
import styles from "./teamLeaderboard.module.scss";

interface LeaderboardEntry {
  rank: number;
  name: string;
  initials: string;
  poCount: number;
  poValue: number;
  savings: number;
  avatarColor?: string;
}

interface TeamLeaderboardProps {
  title?: string;
  data?: LeaderboardEntry[];
  sortBy?: string;
  onSortChange?: (sortBy: string) => void;
  showThisYear?: boolean;
  itemsPerPage?: number;
  onDownload?: () => void;
}

const TeamLeaderboard: React.FC<TeamLeaderboardProps> = ({
  title = "Team Leaderboard",
  data = [],
  sortBy = "total_pos",
  onSortChange,
  showThisYear = false,
  itemsPerPage = 5,
  onDownload,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleExpandClick = () => {
    setIsExpanded(true);
  };

  const handleCloseExpand = () => {
    setIsExpanded(false);
  };

  const handleDownload = () => {
    if (onDownload) {
      onDownload();
      return;
    }

    const excelData: ExcelData = {
      sheetName: title,
      fileName: title.replace(/\s+/g, "_"),
      columns: [
        { header: "Rank", key: "rank", width: 10 },
        { header: "Buyer Name", key: "name", width: 30 },
        { header: "PO Count", key: "poCount", width: 15 },
        { header: "PO Value (USD)", key: "poValue", width: 20 },
        { header: "Savings (USD)", key: "savings", width: 20 },
      ],
      data: data,
    };

    exportToExcel(excelData);
  };

  const displayData = useMemo(() => {
    return data.slice(0, itemsPerPage);
  }, [data, itemsPerPage]);

  const handleSortChange = (event: SelectChangeEvent<string>) => {
    if (onSortChange) {
      onSortChange(event.target.value);
    }
  };
  const getAvatarColor = (rank: number): string => {
    const colors = [
      "#00d2aa", // teal
      "#5b6b9e", // blue
      "#ffa94d", // orange
      "#7b8fc4", // light blue
      "#00d2aa", // teal
    ];
    return colors[rank - 1] || "#5b6b9e";
  };

  const getRankStyle = (rank: number) => {
    if (rank === 1) return styles.rankGold;
    if (rank === 2) return styles.rankSilver;
    if (rank === 3) return styles.rankBronze;
    return styles.rankDefault;
  };

  const getRowStyle = (rank: number) => {
    if (rank <= 3) return styles.topPerformer;
    return "";
  };

  const formatSavings = (value: number): string => {
    if (value >= 1000000) return `$${(value / 1000000).toFixed(2)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(2)}K`;
    return `$${value.toFixed(2)}`;
  };

  if (!data || data.length === 0) {
    return (
      <Box className={styles.container}>
        <Box className={styles.header}>
          <Typography variant="h6" className={styles.title}>
            🏆 {title}
          </Typography>
          {showThisYear && (
            <Chip
              label="This Year"
              size="small"
              sx={{
                backgroundColor: "#e6faf5",
                color: "#00d2aa",
                fontWeight: 600,
                fontSize: "0.75rem",
                marginLeft: "auto",
                marginRight: 1,
              }}
            />
          )}
          <Box className={styles.headerActions}>
            {onSortChange && (
              <FormControl size="small" className={styles.sortSelect}>
                <Select
                  value={sortBy}
                  onChange={handleSortChange}
                  displayEmpty
                  sx={{
                    fontSize: "13px",
                    fontFamily: "G42 Sans, sans-serif",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#e0e0e0",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#00d2aa",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#00d2aa",
                    },
                  }}
                >
                  <MenuItem value="total_pos">Sort by PO Count</MenuItem>
                  <MenuItem value="total_po_value">Sort by PO Value</MenuItem>
                  <MenuItem value="total_savings_amount_value">
                    Sort by Savings
                  </MenuItem>
                </Select>
              </FormControl>
            )}
          </Box>
        </Box>
        <Box
          className={styles.leaderboardContainer}
          display="flex"
          alignItems="center"
          justifyContent="center"
          minHeight={200}
        >
          <Typography variant="body2" color="textSecondary">
            No data available
          </Typography>
        </Box>
      </Box>
    );
  }

  const renderLeaderboard = (dataToRender: LeaderboardEntry[], isModal = false) => (
    <Box className={isModal ? styles.modalLeaderboardContainer : styles.leaderboardContainer}>
      {dataToRender.map((entry) => (
        <Box
          key={entry.rank}
          className={`${styles.leaderboardRow} ${getRowStyle(entry.rank)}`}
        >
          <Box className={`${styles.rank} ${getRankStyle(entry.rank)}`}>
            {entry.rank}
          </Box>
          <Avatar
            className={styles.avatar}
            sx={{
              backgroundColor:
                entry.avatarColor || getAvatarColor(entry.rank),
            }}
          >
            {entry.initials}
          </Avatar>
          <Box className={styles.userInfo}>
            <Typography className={styles.userName}>{entry.name}</Typography>
            <Typography className={styles.userStats}>
              {entry.poCount} POs
            </Typography>
          </Box>
          <Box className={styles.poInfo}>
            <Typography className={styles.savingsLabel}>PO Value</Typography>
            <Typography className={styles.savingsAmount}>
              {formatSavings(entry.poValue)}
            </Typography>
          </Box>
          <Box className={styles.savingsInfo}>
            <Typography className={styles.savingsLabel}>Savings</Typography>
            <Typography className={styles.savingsAmount}>
              {formatSavings(entry.savings)}
            </Typography>
          </Box>
        </Box>
      ))}
    </Box>
  );

  return (
    <>
      <Box className={styles.container}>
        <Box className={styles.header}>
          <Typography variant="h6" className={styles.title}>
            🏆 {title}
          </Typography>
          {showThisYear && (
            <Chip
              label="This Year"
              size="small"
              sx={{
                backgroundColor: "#e6faf5",
                color: "#00d2aa",
                fontWeight: 500,
                fontSize: "0.75rem",
                marginLeft: "auto",
                marginRight: 1,
              }}
            />
          )}
          <Box className={styles.headerActions}>
            {data.length > itemsPerPage && (
              <IconButton
                onClick={handleExpandClick}
                size="small"
                sx={{
                  color: "#5b6b9e",
                  "&:hover": {
                    backgroundColor: "rgba(91, 107, 158, 0.1)",
                  },
                }}
                aria-label="expand"
              >
                <OpenInFullIcon fontSize="small" />
              </IconButton>
            )}
            <IconButton
              onClick={handleDownload}
              size="small"
              sx={{
                color: "#5b6b9e",
                "&:hover": {
                  backgroundColor: "rgba(91, 107, 158, 0.1)",
                },
              }}
              aria-label="download"
            >
              <FileDownloadOutlinedIcon fontSize="small" />
            </IconButton>
            {onSortChange && (
              <FormControl size="small" className={styles.sortSelect}>
                <Select
                  value={sortBy}
                  onChange={handleSortChange}
                  displayEmpty
                  sx={{
                    fontSize: "13px",
                    fontFamily: "G42 Sans, sans-serif",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#e0e0e0",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#00d2aa",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#00d2aa",
                    },
                  }}
                >
                  <MenuItem value="total_pos">Sort by PO Count</MenuItem>
                  <MenuItem value="total_po_value">Sort by PO Value</MenuItem>
                  <MenuItem value="total_savings_amount_value">
                    Sort by Savings
                  </MenuItem>
                </Select>
              </FormControl>
            )}
          </Box>
        </Box>

        {renderLeaderboard(displayData)}
      </Box>

      <Dialog
        open={isExpanded}
        onClose={handleCloseExpand}
        maxWidth={false}
        fullScreen
        className={styles.expandDialog}
      >
        <DialogTitle className={styles.dialogTitle}>
          <Box className={styles.dialogTitleLeft}>
            <Typography variant="h6" className={styles.dialogTitleText}>
              🏆 {title}
            </Typography>
            {showThisYear && (
              <Chip
                label="This Year"
                size="small"
                sx={{
                  backgroundColor: "#e6faf5",
                  color: "#00d2aa",
                  fontWeight: 500,
                  fontSize: "0.75rem",
                  marginLeft: 2,
                }}
              />
            )}
          </Box>
          <Box className={styles.dialogActions}>
            <IconButton
              onClick={handleDownload}
              size="small"
              sx={{
                color: "#5b6b9e",
                "&:hover": {
                  backgroundColor: "rgba(91, 107, 158, 0.1)",
                },
              }}
              aria-label="download"
            >
              <FileDownloadOutlinedIcon fontSize="small" />
            </IconButton>
            {onSortChange && (
              <FormControl size="small" className={styles.sortSelect}>
                <Select
                  value={sortBy}
                  onChange={handleSortChange}
                  displayEmpty
                  sx={{
                    fontSize: "13px",
                    fontFamily: "G42 Sans, sans-serif",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#e0e0e0",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#00d2aa",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#00d2aa",
                    },
                  }}
                >
                  <MenuItem value="total_pos">Sort by PO Count</MenuItem>
                  <MenuItem value="total_po_value">Sort by PO Value</MenuItem>
                  <MenuItem value="total_savings_amount_value">
                    Sort by Savings
                  </MenuItem>
                </Select>
              </FormControl>
            )}
            <IconButton
              aria-label="close"
              onClick={handleCloseExpand}
              sx={{
                color: "#5b6b9e",
                "&:hover": {
                  backgroundColor: "rgba(91, 107, 158, 0.1)",
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent className={styles.dialogContent}>
          {renderLeaderboard(data, true)}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TeamLeaderboard;
